.overview {
  grid-column: 1 / -1;
  min-width: 0;
}

/* Five `KpiTile`s. The tiles draw themselves; this lays them out and carries the one state a tile
   cannot: a read that has not answered. Nothing here is named `figure*`, `label*` or `note*` -
   those belong to `KpiTile`'s stylesheet and the browser gate finds a tile's parts by them. */
.metrics {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--gap);
  margin: 0;
}

.stat {
  display: flex;
  min-width: 0;
}

.stat > * {
  flex: 1;
}

/* A tile with something to chase gets a heavier edge in the warn tint; the words (the note and the
   link) carry the meaning, the edge only helps the eye find it. */
.gap > [data-kpi] {
  border-color: var(--warn);
}

/* A bone where the number will be, drawn on the figure line so the tile does not change height when
   the answer arrives. */
.pending [data-kpi] > :nth-child(2)::before {
  content: "";
  display: block;
  width: 3ch;
  height: 0.8em;
  margin-top: 0.15em;
  border-radius: 4px;
  background: var(--panel-3);
}

.drill {
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: var(--t-min);
  text-decoration: underline;
  cursor: pointer;
}

.stateSummary,
.note {
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.stateSummary {
  margin: 10px 0 0;
  max-width: 100ch;
}

.note {
  margin: 0 0 10px;
  max-width: 75ch;
}

.table + .note {
  margin: 12px 0 0;
}

.sitePanel {
  align-self: start;
  min-width: 0;
}

.table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  color: var(--ink-2);
  font-size: var(--t-min);
}

.table th,
.table td {
  padding: 10px 6px;
  border-bottom: 1px solid var(--line);
  text-align: left;
  overflow-wrap: anywhere;
}

.table thead th {
  color: var(--muted);
  font-weight: 500;
}

.table tbody th {
  font-weight: 500;
}

.amount {
  display: block;
  margin-bottom: 5px;
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--ink);
}

.track {
  display: block;
  height: 5px;
  background: var(--panel-3);
  border-radius: 3px;
  overflow: hidden;
}

.fill {
  display: block;
  height: 100%;
  background: var(--warn);
  border-radius: inherit;
}


/* Five tiles: a row of five while the body is wide, then three and two, then two and two and one.
   The label is allowed to wrap here: at three and two across a tile is too narrow for
   "SUBMITTED WINDOWS" on one line, and a label cut with an ellipsis is a label that is not read. */
@media (width <= 1100px) {
  .metrics {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .stat [class*="_label_"] {
    white-space: normal;
  }
}

@media (width <= 560px) {
  .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stat:last-child {
    grid-column: 1 / -1;
  }
}

/* ----------------------------------------------------------------------- the screen's own layout */

/* The verdict: one pill and the sentence that says what the gap means, above the tabs so it is read
   on every one of them. */
.verdictBar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px 12px;
  grid-column: 1 / -1;
}

.verdictText {
  flex: 1 1 40ch;
  min-width: 0;
}

.verdictText > p {
  margin: 0;
  max-width: 92ch;
  font-size: var(--t-dense);
  line-height: 1.5;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

.verdictText > p + p {
  margin-top: 4px;
}

.verdictGap {
  color: var(--warn);
  font-weight: 600;
}

.tabArea {
  grid-column: 1 / -1;
  min-width: 0;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-width: 0;
}

/* A panel is a column flex box with `min-height: 0`; in a stack with no fixed height it must keep
   its content height, or a long panel would be squeezed against its neighbours. */
.stack > * {
  flex: none;
}

.pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--gap);
  align-items: start;
}

@media (width <= 900px) {
  .pair {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* "Basis and limits": the long honesty copy, closed by default and ranked below the verdict. */
.basis {
  margin: 0;
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.5;
}

.basis > * + * {
  margin-top: 8px;
}

.basis p {
  margin: 0;
  max-width: 80ch;
  overflow-wrap: anywhere;
}

/* What the link narrowed the streams to (a site and/or a window state), above the tabs so it is
   read on every one of them. Quiet: it explains a view, it does not warn. */
.narrowing {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  padding: 6px 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel-2);
  font-size: var(--t-dense);
  color: var(--ink-2);
}

.narrowing > p {
  margin: 0;
  flex: 1 1 40ch;
  min-width: 0;
  overflow-wrap: anywhere;
}
